import { useState, useEffect, useCallback } from 'react';
import { apiJson, apiFetch } from '../../lib/api';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

interface OrgEntry {
  id: string;
  slug: string;
  display_name: string;
  compute_mode: string;
  member_count: number;
  created_at: string;
  max_members?: number;
  max_monthly_cost_usd?: number;
}

interface MemberEntry {
  user_id: string;
  login: string;
  org_role: string;
  joined_at: string;
  avatar_url: string | null;
}

interface InvitationEntry {
  token: string;
  recipient: string;
  org_role: string;
  status: string;
  created_at: string;
  invited_by: string;
  expires_at: string;
}

type InviteMode = 'github' | 'email' | 'link';

export function OrgManager() {
  const [orgs, setOrgs] = useState<OrgEntry[]>([]);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [members, setMembers] = useState<MemberEntry[]>([]);
  const [invitations, setInvitations] = useState<InvitationEntry[]>([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newCompute, setNewCompute] = useState('shared');
  const [newCostLimit, setNewCostLimit] = useState('');
  const [newMaxMembers, setNewMaxMembers] = useState('');
  const [inviteLogin, setInviteLogin] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMode, setInviteMode] = useState<InviteMode>('github');
  const [inviteUrl, setInviteUrl] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [settingsName, setSettingsName] = useState('');
  const [settingsMaxMembers, setSettingsMaxMembers] = useState('');
  const [settingsCompute, setSettingsCompute] = useState('shared');
  const [settingsCostLimit, setSettingsCostLimit] = useState('');
  const [editingRole, setEditingRole] = useState<string | null>(null);

  const loadOrgs = useCallback(() => {
    apiJson<{ orgs: OrgEntry[] }>('/api/admin/orgs')
      .then(d => setOrgs(d.orgs || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const loadMembers = useCallback((orgId: string) => {
    apiJson<{ members: MemberEntry[] }>(`/api/admin/orgs/${orgId}/members`)
      .then(d => setMembers(d.members || []))
      .catch(() => {});
  }, []);

  const loadInvitations = useCallback((orgId: string) => {
    apiJson<{ invitations: InvitationEntry[] }>(`/api/admin/orgs/${orgId}/invitations`)
      .then(d => setInvitations(d.invitations || []))
      .catch(() => setInvitations([]));
  }, []);

  const toggleExpand = (orgId: string) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
      setShowSettings(false);
    } else {
      setExpandedOrg(orgId);
      loadMembers(orgId);
      loadInvitations(orgId);
      setInviteUrl('');
      setInviteLogin('');
      setInviteEmail('');
      setEmailSent(false);
      setCopied(false);
      setShowSettings(false);
      setSuccessMsg('');
      setError('');
      // Pre-fill settings
      const org = orgs.find(o => o.id === orgId);
      if (org) {
        setSettingsName(org.display_name);
        setSettingsMaxMembers(org.max_members ? String(org.max_members) : '');
        setSettingsCompute(org.compute_mode || 'shared');
        setSettingsCostLimit(org.max_monthly_cost_usd ? String(org.max_monthly_cost_usd) : '');
      }
    }
  };

  const createOrg = async () => {
    setError('');
    const body: Record<string, unknown> = { display_name: newName };
    if (newSlug) body.slug = newSlug;
    if (newCompute) body.compute_mode = newCompute;
    if (newCostLimit) body.max_monthly_cost_usd = parseFloat(newCostLimit);
    if (newMaxMembers) body.max_members = parseInt(newMaxMembers);

    const resp = await apiFetch('/api/admin/orgs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (data.ok) {
      setShowNewForm(false);
      setNewName('');
      setNewSlug('');
      loadOrgs();
    } else {
      setError(data.error || 'Failed to create org');
    }
  };

  const deleteOrg = async (id: string) => {
    if (!confirm(`Delete organization "${id}"?`)) return;
    await apiFetch(`/api/admin/orgs/${id}`, { method: 'DELETE' });
    setExpandedOrg(null);
    loadOrgs();
  };

  const inviteMember = async (orgId: string) => {
    setError('');
    setInviteUrl('');
    setEmailSent(false);
    setCopied(false);
    const body: Record<string, unknown> = { org_role: inviteRole };

    if (inviteMode === 'github') {
      body.github_login = inviteLogin;
    } else if (inviteMode === 'email') {
      body.email = inviteEmail;
      body.github_login = '';
    }
    // link mode: no github_login or email — creates unbound invite

    const resp = await apiFetch(`/api/admin/orgs/${orgId}/invite`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (data.ok) {
      setInviteUrl(data.invite_url || '');
      setEmailSent(!!data.email_sent);
      setInviteLogin('');
      setInviteEmail('');
      loadInvitations(orgId);
      if (inviteMode === 'github') {
        setSuccessMsg(`Invite created for @${body.github_login}`);
      } else if (inviteMode === 'email') {
        setSuccessMsg(data.email_sent ? `Invite email sent to ${body.email}` : `Invite created (email delivery not configured)`);
      } else {
        setSuccessMsg('Invite link generated');
      }
      setTimeout(() => setSuccessMsg(''), 4000);
    } else {
      setError(data.error || 'Failed to send invite');
    }
  };

  const revokeInvitation = async (token: string) => {
    setError('');
    try {
      const resp = await apiFetch(`/api/admin/invitations/${token}`, { method: 'DELETE' });
      const data = await resp.json();
      if (data.ok || resp.ok) {
        if (expandedOrg) loadInvitations(expandedOrg);
      } else {
        setError(data.error || 'Failed to revoke invitation');
      }
    } catch {
      setError('Failed to revoke invitation');
    }
  };

  const removeMember = async (orgId: string, userId: string) => {
    if (!confirm('Remove this member?')) return;
    await apiFetch(`/api/admin/orgs/${orgId}/members/${userId}`, { method: 'DELETE' });
    loadMembers(orgId);
    loadOrgs();
  };

  const updateMemberRole = async (orgId: string, userId: string, newRole: string) => {
    setError('');
    setSuccessMsg('');
    try {
      const resp = await apiFetch(`/api/admin/orgs/${orgId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ org_role: newRole }),
      });
      const data = await resp.json();
      if (data.ok || resp.ok) {
        setSuccessMsg('Role updated');
        loadMembers(orgId);
        setTimeout(() => setSuccessMsg(''), 2000);
      } else {
        setError(data.error || 'Failed to update role');
      }
    } catch {
      setError('Failed to update role');
    }
    setEditingRole(null);
  };

  const saveOrgSettings = async (orgId: string) => {
    setError('');
    setSuccessMsg('');
    const body: Record<string, unknown> = {};
    if (settingsName) body.display_name = settingsName;
    if (settingsMaxMembers) body.max_members = parseInt(settingsMaxMembers);
    if (settingsCompute) body.compute_mode = settingsCompute;
    if (settingsCostLimit) body.max_monthly_cost_usd = parseFloat(settingsCostLimit);

    try {
      const resp = await apiFetch(`/api/admin/orgs/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (data.ok || resp.ok) {
        setSuccessMsg('Settings saved');
        loadOrgs();
        setTimeout(() => setSuccessMsg(''), 2000);
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch {
      setError('Failed to save settings');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 4,
    color: '#f8f8f2',
    fontSize: 13,
    fontFamily: FONT,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'all 0.15s ease',
  };

  const btnStyle: React.CSSProperties = {
    padding: '8px 16px',
    background: '#4f8fff',
    color: '#0d0d0d',
    border: 'none',
    borderRadius: 4,
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };

  const btnGhost: React.CSSProperties = {
    padding: '8px 16px',
    background: 'transparent',
    color: '#a8b2b8',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 4,
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: 400,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    color: '#6272a4',
    display: 'block',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '8px 8px',
    fontSize: 10,
    color: '#6272a4',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const statusBadge = (status: string): React.CSSProperties => {
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      pending: { bg: 'rgba(255,183,77,0.1)', border: 'rgba(255,183,77,0.25)', text: '#ffb74d' },
      accepted: { bg: 'rgba(52,211,153,0.1)', border: 'rgba(52,211,153,0.25)', text: '#34d399' },
      revoked: { bg: 'rgba(255,85,85,0.1)', border: 'rgba(255,85,85,0.25)', text: '#ff5555' },
      expired: { bg: 'rgba(107,114,128,0.1)', border: 'rgba(107,114,128,0.25)', text: '#6b7280' },
    };
    const c = colors[status] || colors.pending;
    return {
      fontSize: 10,
      padding: '2px 8px',
      borderRadius: 4,
      background: c.bg,
      border: `1px solid ${c.border}`,
      color: c.text,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: FONT,
    };
  };

  const roleBadge: React.CSSProperties = {
    fontSize: 10,
    padding: '2px 8px',
    borderRadius: 4,
    background: 'rgba(189,147,249,0.1)',
    border: '1px solid rgba(189,147,249,0.25)',
    color: '#bd93f9',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: FONT,
  };

  const modeBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    background: active ? 'rgba(79,143,255,0.12)' : 'transparent',
    border: `1px solid ${active ? 'rgba(79,143,255,0.3)' : 'rgba(255,255,255,0.06)'}`,
    borderRadius: 4,
    color: active ? '#4f8fff' : '#6272a4',
    fontSize: 11,
    fontFamily: FONT,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  const isInviteReady = () => {
    if (inviteMode === 'github') return !!inviteLogin;
    if (inviteMode === 'email') return !!inviteEmail;
    return true; // link mode always ready
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 11, color: '#6272a4', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {orgs.length} organization{orgs.length !== 1 ? 's' : ''}
        </span>
        <button onClick={() => setShowNewForm(!showNewForm)} style={btnStyle}>
          + New Org
        </button>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', background: 'rgba(255,85,85,0.08)', border: '1px solid rgba(255,85,85,0.2)',
          borderRadius: 8, color: '#ff5555', fontSize: 12, marginBottom: 16, fontFamily: FONT,
        }}>
          {error}
        </div>
      )}

      {successMsg && (
        <div style={{
          padding: '8px 12px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)',
          borderRadius: 8, color: '#34d399', fontSize: 12, marginBottom: 16, fontFamily: FONT,
        }}>
          {successMsg}
        </div>
      )}

      {/* New org form */}
      {showNewForm && (
        <div style={{
          background: '#282a36', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
          padding: 24, marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 16 }}>
            Create Organization
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Display Name *</label>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Acme Corp"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Slug (optional)</label>
              <input
                value={newSlug}
                onChange={e => setNewSlug(e.target.value)}
                placeholder="acme-corp"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Compute Mode</label>
              <select
                value={newCompute}
                onChange={e => setNewCompute(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="shared">Shared</option>
                <option value="dedicated">Dedicated</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Cost Limit (USD/mo)</label>
              <input
                value={newCostLimit}
                onChange={e => setNewCostLimit(e.target.value)}
                placeholder="500"
                type="number"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={createOrg} disabled={!newName} style={{ ...btnStyle, opacity: newName ? 1 : 0.5 }}>
              Create
            </button>
            <button onClick={() => setShowNewForm(false)} style={btnGhost}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Org list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {orgs.map(org => (
          <div key={org.id} style={{
            background: '#1e1e2e', border: `1px solid ${expandedOrg === org.id ? 'rgba(79,143,255,0.25)' : 'rgba(255,255,255,0.06)'}`,
            borderRadius: 8, overflow: 'hidden', transition: 'all 0.15s ease',
          }}>
            {/* Row header */}
            <div
              onClick={() => toggleExpand(org.id)}
              style={{
                padding: '14px 16px',
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div>
                <div style={{ fontWeight: 600, color: '#f8f8f2', fontSize: 13 }}>{org.display_name}</div>
                <div style={{ fontSize: 11, color: '#6272a4', marginTop: 2 }}>{org.slug}</div>
              </div>
              <span style={{
                fontSize: 10, color: '#4f8fff', padding: '2px 8px',
                background: 'rgba(79,143,255,0.08)', borderRadius: 4, justifySelf: 'start',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {org.compute_mode}
              </span>
              <span style={{ fontSize: 11, color: '#a8b2b8' }}>{org.member_count} members</span>
              <span style={{ fontSize: 11, color: '#6272a4', justifySelf: 'end' }}>
                {new Date(org.created_at).toLocaleDateString()}
              </span>
            </div>

            {/* Expanded detail */}
            {expandedOrg === org.id && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: 16 }}>

                {/* ── Org Settings Panel ── */}
                <div style={{ marginBottom: 24 }}>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    style={{
                      ...btnGhost,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: showSettings ? 12 : 0,
                    }}
                  >
                    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                    </svg>
                    {showSettings ? 'Hide Settings' : 'Org Settings'}
                  </button>

                  {showSettings && (
                    <div style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 8,
                      padding: 16,
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div>
                          <label style={labelStyle}>Display Name</label>
                          <input
                            value={settingsName}
                            onChange={e => setSettingsName(e.target.value)}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Max Members</label>
                          <input
                            value={settingsMaxMembers}
                            onChange={e => setSettingsMaxMembers(e.target.value)}
                            type="number"
                            placeholder="Unlimited"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Compute Mode</label>
                          <select
                            value={settingsCompute}
                            onChange={e => setSettingsCompute(e.target.value)}
                            style={{ ...inputStyle, cursor: 'pointer' }}
                          >
                            <option value="shared">Shared</option>
                            <option value="dedicated">Dedicated</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Cost Limit (USD/mo)</label>
                          <input
                            value={settingsCostLimit}
                            onChange={e => setSettingsCostLimit(e.target.value)}
                            type="number"
                            placeholder="No limit"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => saveOrgSettings(org.id)}
                        style={btnStyle}
                      >
                        Save Settings
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Members table ── */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 12 }}>Members</div>
                  {members.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <th style={thStyle}>User</th>
                          <th style={thStyle}>Role</th>
                          <th style={thStyle}>Joined</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(m => (
                          <tr key={m.user_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                            <td style={{ padding: '8px', fontSize: 13, color: '#f8f8f2' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {m.avatar_url && (
                                  <img src={m.avatar_url} width={20} height={20} style={{ borderRadius: '50%' }} />
                                )}
                                @{m.login}
                              </span>
                            </td>
                            <td style={{ padding: '8px' }}>
                              {editingRole === m.user_id ? (
                                <select
                                  value={m.org_role}
                                  onChange={e => updateMemberRole(org.id, m.user_id, e.target.value)}
                                  onBlur={() => setEditingRole(null)}
                                  autoFocus
                                  style={{
                                    ...inputStyle,
                                    width: 'auto',
                                    padding: '4px 8px',
                                    fontSize: 11,
                                    cursor: 'pointer',
                                  }}
                                >
                                  <option value="owner">Owner</option>
                                  <option value="admin">Admin</option>
                                  <option value="member">Member</option>
                                  <option value="viewer">Viewer</option>
                                </select>
                              ) : (
                                <span
                                  onClick={() => setEditingRole(m.user_id)}
                                  style={{
                                    fontSize: 11,
                                    color: '#bd93f9',
                                    cursor: 'pointer',
                                    padding: '2px 8px',
                                    background: 'rgba(189,147,249,0.08)',
                                    borderRadius: 4,
                                    border: '1px solid rgba(189,147,249,0.15)',
                                    transition: 'all 0.15s ease',
                                  }}
                                  title="Click to change role"
                                >
                                  {m.org_role}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '8px', fontSize: 11, color: '#6272a4' }}>
                              {new Date(m.joined_at).toLocaleDateString()}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <button
                                onClick={() => removeMember(org.id, m.user_id)}
                                style={{
                                  padding: '4px 10px', background: 'transparent',
                                  border: '1px solid rgba(255,85,85,0.3)', borderRadius: 4,
                                  color: '#ff5555', fontSize: 11, fontFamily: FONT, cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ fontSize: 11, color: '#6272a4', fontFamily: FONT }}>No members yet</p>
                  )}
                </div>

                {/* ── Pending Invitations ── */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 12 }}>
                    Pending Invitations
                  </div>
                  {invitations.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <th style={thStyle}>Recipient</th>
                          <th style={thStyle}>Role</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Invited By</th>
                          <th style={thStyle}>Created</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {invitations.map(inv => {
                          const isExpired = inv.status === 'pending' && inv.expires_at && new Date(inv.expires_at) < new Date();
                          return (
                            <tr key={inv.token} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                              <td style={{ padding: '8px', fontSize: 12, color: '#f8f8f2', fontFamily: FONT }}>
                                {inv.recipient || <span style={{ color: '#6272a4', fontStyle: 'italic' }}>Link invite</span>}
                              </td>
                              <td style={{ padding: '8px' }}>
                                <span style={roleBadge}>{inv.org_role}</span>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <span style={statusBadge(isExpired ? 'expired' : inv.status)}>
                                  {isExpired ? 'expired' : inv.status}
                                </span>
                              </td>
                              <td style={{ padding: '8px', fontSize: 11, color: '#a8b2b8' }}>
                                {inv.invited_by || '-'}
                              </td>
                              <td style={{ padding: '8px', fontSize: 11, color: '#6272a4' }}>
                                {new Date(inv.created_at).toLocaleDateString()}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>
                                {inv.status === 'pending' && !isExpired && (
                                  <button
                                    onClick={() => revokeInvitation(inv.token)}
                                    style={{
                                      padding: '4px 10px', background: 'transparent',
                                      border: '1px solid rgba(255,85,85,0.3)', borderRadius: 4,
                                      color: '#ff5555', fontSize: 11, fontFamily: FONT, cursor: 'pointer',
                                      transition: 'all 0.15s ease',
                                    }}
                                  >
                                    Revoke
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ fontSize: 11, color: '#6272a4', fontFamily: FONT }}>No pending invitations</p>
                  )}
                </div>

                {/* ── Invite form ── */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 12 }}>Invite Member</div>

                  {/* Mode toggle */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                    <button onClick={() => setInviteMode('github')} style={modeBtnStyle(inviteMode === 'github')}>
                      GitHub Login
                    </button>
                    <button onClick={() => setInviteMode('email')} style={modeBtnStyle(inviteMode === 'email')}>
                      Email
                    </button>
                    <button onClick={() => setInviteMode('link')} style={modeBtnStyle(inviteMode === 'link')}>
                      Link Only
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    {inviteMode === 'github' && (
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>GitHub Login</label>
                        <input
                          value={inviteLogin}
                          onChange={e => setInviteLogin(e.target.value)}
                          placeholder="username"
                          style={inputStyle}
                        />
                      </div>
                    )}
                    {inviteMode === 'email' && (
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Email Address</label>
                        <input
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e.target.value)}
                          placeholder="user@example.com"
                          type="email"
                          style={inputStyle}
                        />
                      </div>
                    )}
                    {inviteMode === 'link' && (
                      <div style={{ flex: 1, fontSize: 11, color: '#6272a4', fontFamily: FONT, padding: '8px 0' }}>
                        Generates a shareable invite link with no user binding
                      </div>
                    )}
                    <div style={{ width: 120 }}>
                      <label style={labelStyle}>Role</label>
                      <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                        <option value="viewer">Viewer</option>
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    </div>
                    <button
                      onClick={() => inviteMember(org.id)}
                      disabled={!isInviteReady()}
                      style={{ ...btnStyle, opacity: isInviteReady() ? 1 : 0.5, whiteSpace: 'nowrap' }}
                    >
                      {inviteMode === 'link' ? 'Generate Link' : 'Send Invite'}
                    </button>
                  </div>

                  {inviteUrl && (
                    <div style={{
                      marginTop: 8, padding: '10px 12px', background: 'rgba(79,143,255,0.06)',
                      border: '1px solid rgba(79,143,255,0.2)', borderRadius: 8,
                      display: 'flex', flexDirection: 'column', gap: 6,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: '#4f8fff', wordBreak: 'break-all', fontFamily: FONT, flex: 1 }}>
                        {inviteUrl}
                      </span>
                      <button
                        onClick={() => copyToClipboard(inviteUrl)}
                        style={{
                          padding: '4px 10px',
                          background: copied ? 'rgba(52,211,153,0.12)' : 'rgba(79,143,255,0.12)',
                          border: `1px solid ${copied ? 'rgba(52,211,153,0.25)' : 'rgba(79,143,255,0.25)'}`,
                          borderRadius: 4,
                          color: copied ? '#34d399' : '#4f8fff',
                          fontSize: 11, fontFamily: FONT, cursor: 'pointer',
                          whiteSpace: 'nowrap', transition: 'all 0.15s ease',
                          minWidth: 56,
                        }}
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    {emailSent && (
                      <div style={{ fontSize: 10, color: '#34d399', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="#34d399" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Email invitation sent
                      </div>
                    )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => deleteOrg(org.id)} style={{
                    padding: '6px 16px', background: 'transparent',
                    border: '1px solid rgba(255,85,85,0.3)', borderRadius: 4,
                    color: '#ff5555', fontSize: 12, fontFamily: FONT, cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}>
                    Delete Organization
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {orgs.length === 0 && !showNewForm && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6272a4', fontSize: 13, fontFamily: FONT }}>
            No organizations yet. Click "+ New Org" to create one.
          </div>
        )}
      </div>
    </div>
  );
}
